import { useMemo, useState } from "react";

import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { useAPI, useShipmentPageContext, useSwrFetch } from "hooks";
import { getQuoteTitle } from "utils";

import { QuestionForm } from "./QuestionForm";
import { QuestionsList } from "./QuestionsList";
import { ClientAppEvent } from "../../../../../../constants";

interface Props {
  open: boolean;
  onClose: () => void;
  quote?: ShipperCondensedQuote;
}

type SortOption = "time" | "pending-first" | "answered-first";

type AnsweredQuoteQA = QuoteQA & { answer: QuoteAnswer };

const isAnswered = (qa: QuoteQA): qa is AnsweredQuoteQA => qa.answer !== null;

const byNewestQuestion = (a: QuoteQA, b: QuoteQA) =>
  new Date(b.question.created_at).getTime() -
  new Date(a.question.created_at).getTime();

const byNewestAnswer = (a: AnsweredQuoteQA, b: AnsweredQuoteQA) =>
  new Date(b.answer.created_at).getTime() -
  new Date(a.answer.created_at).getTime();

export function QuoteQAModal(props: Props) {
  const { open, onClose, quote } = props;
  const { api } = useAPI();
  const { onRefresh } = useShipmentPageContext();
  const posthog = usePostHog();

  const [questionSubmissionError, setQuestionSubmissionError] = useState<
    string | null
  >(null);
  const [sortBy, setSortBy] = useState<SortOption>("time");

  const url = open && quote ? `/quotes/${quote.quote}/qa` : null;

  const {
    data: qaList,
    loading,
    validating,
    error: questionsListFetchError,
    refetch: handleRefresh,
  } = useSwrFetch<QuoteQA[]>({
    url,
    errorMessage: "Failed to fetch questions",
  });

  const handleSendQuestion = async (question: string) => {
    if (!quote) return;

    setQuestionSubmissionError(null);

    try {
      await api("post", `/quotes/${quote.quote}/questions`, { question });

      posthog?.capture(ClientAppEvent.QUOTE_QUESTION_ASKED, {
        quote_id: quote.quote,
        shipment_id: quote.shipment,
      });

      handleRefresh();

      if (onRefresh) {
        onRefresh();
      }
    } catch (e: any) {
      setQuestionSubmissionError(
        e?.response?.data?.message ?? "Failed to submit question",
      );
    }
  };

  // Memoized sorting and counting for performance
  const { sortedQAList, answeredCount, pendingCount } = useMemo(() => {
    const sortQAList = (list: QuoteQA[], sortOption: SortOption): QuoteQA[] => {
      const sortedList = [...list];

      switch (sortOption) {
        case "time":
          return sortedList.sort(
            (a, b) =>
              new Date(a.question.created_at).getTime() -
              new Date(b.question.created_at).getTime(),
          );

        case "pending-first": {
          const pending = sortedList
            .filter((qa) => !isAnswered(qa))
            .sort(byNewestQuestion);
          const answered = sortedList.filter(isAnswered).sort(byNewestAnswer);

          return [...pending, ...answered];
        }

        case "answered-first": {
          const answered = sortedList.filter(isAnswered).sort(byNewestAnswer);
          const pending = sortedList
            .filter((qa) => !isAnswered(qa))
            .sort(byNewestQuestion);

          return [...answered, ...pending];
        }

        default:
          return sortedList;
      }
    };

    const list = qaList ?? [];
    const sorted = sortQAList(list, sortBy);
    const answered = list.filter((qa) => qa.answer).length;
    const pending = list.filter((qa) => !qa.answer).length;

    return {
      sortedQAList: sorted,
      answeredCount: answered,
      pendingCount: pending,
    };
  }, [qaList, sortBy]);

  // Early return after all hooks to avoid Rules of Hooks violation
  if (!quote) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Modal Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Questions for {quote?.created_by_company?.name || "Carrier"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Shipment #{quote.shipment} · {getQuoteTitle(quote)}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh Questions">
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={onClose}
              sx={{
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          {/* Question Count Pills */}
          <Stack direction="row" spacing={1}>
            <Chip
              label={`${answeredCount} Answered`}
              size="small"
              sx={{
                backgroundColor: "success.light",
                color: "white",
                fontWeight: "semibold",
                borderRadius: 3,
              }}
            />
            <Chip
              label={`${pendingCount} Pending`}
              size="small"
              sx={{
                backgroundColor: "#fff3cd",
                color: "#856404",
                fontWeight: "semibold",
                borderRadius: 3,
              }}
            />
          </Stack>
        </Box>
      </Box>

      {/* Questions List */}
      <DialogContent
        sx={{
          flex: 1,
          overflow: "auto",
          p: 3,
        }}
      >
        {/* Sort Bar */}
        <Box sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value as SortOption)}
            >
              <MenuItem value="time">Time Asked</MenuItem>
              <MenuItem value="pending-first">Pending</MenuItem>
              <MenuItem value="answered-first">Answered</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {validating && !loading && <LinearProgress sx={{ mb: 2 }} />}
        <QuestionsList
          qaList={sortedQAList}
          loading={loading}
          error={questionsListFetchError}
        />
      </DialogContent>

      {/* Ask Question Section */}
      <QuestionForm
        onSubmitQuestion={handleSendQuestion}
        error={questionSubmissionError}
      />
    </Dialog>
  );
}
